import {
  AllocationsFilters,
  AllocationsHeader,
  ConfirmUploadModal,
  EditAgentModal,
  Th,
  UploadResultModal,
  WorkerRow,
} from "@/components/Allocations";
import { EditAgentPayload } from "@/components/Allocations/EditAgentModal";
import { MissingAgent, MissingAgentsModal } from "@/components/Allocations/MissingAgentsModal";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { ALLOCATION_SKILLS, ALLOCATION_SKILL_TYPES } from "@/constants/allocations";
import { useAppData } from "@/context/AppDataContext";
import {
  AllocationSortOrder,
  formatNonAgentValidationErrors,
  hasOnlyMissingAgentErrors,
  parseCsvToShiftCaps,
  parseMissingAgentIds,
  parseTimeToISO,
  sortAllocationsData,
} from "@/helpers/allocations";
import { naturalSort } from "@/helpers/sorting";
import { useCanViewAllocations } from "@/services/AuthService";
import { allocationsService } from "@/services/AllocationsService";
import {
  AllocationSkillId,
  AllocationSkillResponse,
  AllocationsUploadResponse,
  AvailableSkill,
  CapFilter,
  isValidationError,
  ShiftCapsPayload,
  UserSkillAssignment,
  WorkerAllocation,
} from "@/types/allocations";
import { CircleX } from "lucide-react";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";

type MissingAgentsRetry =
  | { type: "upload"; file: File; validatedData?: ShiftCapsPayload[] }
  | {
      type: "edit";
      userId: number;
      payload: EditAgentPayload;
      capsPayload: ShiftCapsPayload;
    };

type ModalState =
  | { type: "none" }
  | { type: "confirmUpload"; file: File }
  | { type: "uploadResult"; result: AllocationsUploadResponse }
  | { type: "editAgent"; userId: number }
  | {
      type: "missingAgents";
      agentIds: number[];
      retry: MissingAgentsRetry;
    };

export const Allocations = () => {
  const canView = useCanViewAllocations();
  const { workers, departmentOptions, refetchWorkers } = useAppData();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [shiftDate, setShiftDate] = useState<string>(moment().format("YYYY-MM-DD"));

  const [originalData, setOriginalData] = useState<WorkerAllocation[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [capFilters, setCapFilters] = useState<CapFilter[]>([]);

  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<AllocationSortOrder>("asc");

  const [modalState, setModalState] = useState<ModalState>({ type: "none" });
  const [isSavingAgent, setIsSavingAgent] = useState(false);

  const [availableSkills, setAvailableSkills] = useState<AvailableSkill[]>([]);
  const [userSkillAssignments, setUserSkillAssignments] = useState<UserSkillAssignment[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const convertToWorkerAllocation = useCallback(
    (response: AllocationSkillResponse): WorkerAllocation => {
      const worker = workers.find((w) => w.userId === response.userId);
      const allocationSkills: { [K in AllocationSkillId]?: number } = {};

      Object.entries(response.allocationSkills).forEach(([skillId, value]) => {
        const id = parseInt(skillId, 10) as AllocationSkillId;
        if (id in ALLOCATION_SKILLS) {
          allocationSkills[id] = value;
        }
      });

      return {
        userId: response.userId,
        name: worker?.name || `${response.userId}`,
        departmentId: worker?.departmentId ?? "",
        department: worker?.department || "",
        shiftStart: response.shiftStart || "",
        shiftEnd: response.shiftEnd || "",
        allocationSkills,
        receivingLeads: response.receivingLeads,
      };
    },
    [workers]
  );

  const fetchData = useCallback(async () => {
    if (!allocationsService.isReady()) return;

    setIsLoading(true);
    setError(null);

    try {
      const [shiftCaps, skills, skillAssignments] = await Promise.all([
        allocationsService.getShiftCaps(shiftDate),
        allocationsService.getAvailableSkills(),
        allocationsService.getUserSkillAssignments(),
      ]);
      const allocations = shiftCaps.map(convertToWorkerAllocation);
      setOriginalData(allocations);
      setAvailableSkills(skills);
      setUserSkillAssignments(skillAssignments);
    } catch (err) {
      console.error("Failed to fetch allocations:", err);
      setError("Failed to load allocations.");
    } finally {
      setIsLoading(false);
    }
  }, [shiftDate, convertToWorkerAllocation]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const allowedDepartmentIds = useMemo(
    () => new Set(departmentOptions.map((d) => d.value)),
    [departmentOptions]
  );

  const allowedDepartments = useMemo(
    () => departmentOptions.map((d) => d.label).sort(naturalSort),
    [departmentOptions]
  );

  const skillOptions = useMemo(
    () =>
      availableSkills
        .map((s) => ({
          value: String(s.allocationSkillId),
          label: s.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [availableSkills]
  );

  const userSkillMap = useMemo(() => {
    const map = new Map<number, Set<string>>();
    for (const assignment of userSkillAssignments) {
      if (!map.has(assignment.intranetUserId)) {
        map.set(assignment.intranetUserId, new Set());
      }
      map.get(assignment.intranetUserId)!.add(String(assignment.allocationSkillId));
    }
    return map;
  }, [userSkillAssignments]);

  const filteredData = useMemo(() => {
    return originalData.filter((worker) => {
      const isInHierarchyDepartment =
        worker.departmentId !== "" && allowedDepartmentIds.has(worker.departmentId);
      if (!isInHierarchyDepartment) return false;

      const matchesSearch =
        searchTerm === "" ||
        worker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        worker.userId.toString().includes(searchTerm);

      const matchesDepartment = selectedDepartments.length === 0 || selectedDepartments.includes(worker.department);

      const matchesSkills =
        selectedSkills.length === 0 || selectedSkills.some((skillId) => userSkillMap.get(worker.userId)?.has(skillId));

      const matchesStatus =
        selectedStatus === "all" ||
        (selectedStatus === "receivingLeads" && worker.receivingLeads) ||
        (selectedStatus === "notReceivingLeads" && !worker.receivingLeads);

      const matchesCaps = capFilters.every((filter) => {
        const cap = worker.allocationSkills[filter.skillId] ?? 0;
        if (filter.min !== undefined && cap < filter.min) return false;
        if (filter.max !== undefined && cap > filter.max) return false;
        return true;
      });

      return matchesSearch && matchesDepartment && matchesSkills && matchesStatus && matchesCaps;
    });
  }, [
    originalData,
    searchTerm,
    selectedDepartments,
    selectedSkills,
    selectedStatus,
    capFilters,
    allowedDepartmentIds,
    userSkillMap,
  ]);

  const sortedData = useMemo(() => {
    return sortAllocationsData(filteredData, sortBy, sortOrder);
  }, [filteredData, sortBy, sortOrder]);

  const handleSort = useCallback(
    (key: string) => {
      if (sortBy === key) {
        setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(key);
        setSortOrder("asc");
      }
    },
    [sortBy]
  );

  const getWorkerData = useCallback(
    (userId: number): WorkerAllocation => {
      return originalData.find((w) => w.userId === userId) as WorkerAllocation;
    },
    [originalData]
  );

  const handleReceivingLeadsChange = useCallback(
    async (userId: number, receivingLeads: boolean) => {
      const toastId = toast.loading("Updating receiving leads status");
      const worker = getWorkerData(userId);
      try {
        await allocationsService.toggleReceivingLeads(userId, receivingLeads);
        setOriginalData((prev) => prev.map((w) => (w.userId === userId ? { ...w, receivingLeads } : w)));
        toast.success(`Receiving leads status updated${worker ? ` for ${worker.name}` : ""}`, { id: toastId });
      } catch (err) {
        console.error("Failed to toggle receiving leads:", err);
        toast.error("Failed to update receiving leads status", { id: toastId });
      }
    },
    [getWorkerData]
  );

  const getUserSkillIds = useCallback(
    (userId: number): number[] => {
      return userSkillAssignments
        .filter((assignment) => assignment.intranetUserId === userId)
        .map((assignment) => assignment.allocationSkillId);
    },
    [userSkillAssignments]
  );

  const skillLookupMap = useMemo(
    () => new Map(availableSkills.map((s) => [s.allocationSkillId, s])),
    [availableSkills]
  );

  const getUserSkillsByType = useCallback(
    (userId: number): { typeName: string; skillNames: string[] }[] => {
      const skillIds = getUserSkillIds(userId);
      const grouped = new Map<string, string[]>();

      for (const type of Object.keys(ALLOCATION_SKILL_TYPES)) {
        const typeName = ALLOCATION_SKILL_TYPES[parseInt(type, 10)];
        if (!grouped.has(typeName)) {
          grouped.set(typeName, []);
        }
      }

      for (const id of skillIds) {
        const skill = skillLookupMap.get(id);
        if (!skill) continue;
        const typeName = ALLOCATION_SKILL_TYPES[skill.allocationSkillTypeId];

        if (!grouped.has(typeName)) {
          grouped.set(typeName, []);
        }
        grouped.get(typeName)!.push(skill.name);
      }

      return Array.from(grouped.entries())
        .map(([typeName, names]) => ({
          typeName,
          skillNames: names.sort(),
        }))
        .sort((a, b) => a.typeName.localeCompare(b.typeName));
    },
    [getUserSkillIds, skillLookupMap]
  );

  const editAgentUserId = modalState.type === "editAgent" ? modalState.userId : null;

  const editAgentWorker = useMemo(() => {
    if (editAgentUserId === null)
      return {
        userId: 0,
        name: "",
        departmentId: "",
        department: "",
        shiftStart: "",
        shiftEnd: "",
        allocationSkills: {},
        receivingLeads: false,
      } as WorkerAllocation;
    return getWorkerData(editAgentUserId);
  }, [editAgentUserId, getWorkerData]);

  const editAgentUserSkillIds = useMemo(() => {
    if (editAgentUserId === null) return [];
    return getUserSkillIds(editAgentUserId);
  }, [editAgentUserId, getUserSkillIds]);

  const handleEditAgent = useCallback((userId: number) => {
    setModalState({ type: "editAgent", userId });
  }, []);

  const handleSaveAgent = useCallback(
    async (userId: number, payload: EditAgentPayload) => {
      setIsSavingAgent(true);
      const toastId = toast.loading("Saving changes");
      const worker = getWorkerData(userId);
      try {
        if (payload.allocationSkills || payload.shiftStart !== undefined || payload.shiftEnd !== undefined) {
          const allocationSkills: { [skillId: string]: number } = {};
          const skills = payload.allocationSkills ?? worker.allocationSkills;
          Object.entries(skills).forEach(([skillId, value]) => {
            allocationSkills[skillId] = value ?? 0;
          });

          const shiftStart =
            payload.shiftStart !== undefined
              ? parseTimeToISO(payload.shiftStart, worker.shiftStart)
              : worker.shiftStart || null;
          const shiftEnd =
            payload.shiftEnd !== undefined
              ? parseTimeToISO(payload.shiftEnd, worker.shiftEnd)
              : worker.shiftEnd || null;

          const shiftCapsEntry: ShiftCapsPayload = {
            userId,
            shiftStart,
            shiftEnd,
            allocationSkills,
          };

          try {
            await allocationsService.submitShiftCaps([shiftCapsEntry], shiftDate);
          } catch (err) {
            if (isValidationError(err)) {
              if (hasOnlyMissingAgentErrors(err.errors)) {
                const agentIds = parseMissingAgentIds(err.errors);
                if (agentIds) {
                  await refetchWorkers();
                  toast.dismiss(toastId);
                  setModalState({
                    type: "missingAgents",
                    agentIds,
                    retry: {
                      type: "edit",
                      userId,
                      payload,
                      capsPayload: shiftCapsEntry,
                    },
                  });
                  return;
                }
              }
              toast.error(formatNonAgentValidationErrors(err.errors, workers).join(". "), { id: toastId });
              return;
            }
            throw err;
          }

          const updates: Partial<WorkerAllocation> = {};
          if (payload.allocationSkills) {
            updates.allocationSkills = payload.allocationSkills;
          }
          if (shiftStart !== null) updates.shiftStart = shiftStart;
          if (shiftEnd !== null) updates.shiftEnd = shiftEnd;

          setOriginalData((prev) => prev.map((w) => (w.userId === userId ? { ...w, ...updates } : w)));
        }

        if (payload.skillIds) {
          await allocationsService.updateUserSkills({
            userId,
            primarySkillIds: payload.skillIds,
            secondarySkillIds: [],
            originalTarget: 0,
            newTarget: 0,
          });
          setUserSkillAssignments((prev) => {
            const filtered = prev.filter((assignment) => assignment.intranetUserId !== userId);
            const newAssignments = payload.skillIds!.map((skillId) => {
              const skill = availableSkills.find((s) => s.allocationSkillId === skillId);
              return {
                intranetUserId: userId,
                allocationSkillId: skillId,
                isPrimary: true,
                allocationSkillTypeId: skill?.allocationSkillTypeId ?? 0,
                numberOfAllocations: 0,
                cap: null,
              };
            });
            return [...filtered, ...newAssignments];
          });
        }

        toast.success(`Changes saved${worker ? ` for ${worker.name}` : ""}`, {
          id: toastId,
        });
        setModalState({ type: "none" });
      } catch (err) {
        console.error("Failed to save changes:", err);
        toast.error("Failed to save changes", { id: toastId });
      } finally {
        setIsSavingAgent(false);
      }
    },
    [getWorkerData, shiftDate, availableSkills, workers, refetchWorkers]
  );

  const closeModal = useCallback(() => setModalState({ type: "none" }), []);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setModalState({ type: "confirmUpload", file });
    }
    e.target.value = "";
  }, []);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const blob = await allocationsService.downloadTemplate(shiftDate);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `allocation-template-${shiftDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to download template:", err);
      setError("Failed to download template.");
    }
  }, [shiftDate]);

  const handleConfirmUpload = useCallback(async () => {
    if (modalState.type !== "confirmUpload") return;

    setIsSaving(true);
    try {
      let validatedData: ShiftCapsPayload[];
      try {
        const response = await allocationsService.validateTemplate(modalState.file, shiftDate);
        validatedData = response.map((data) => data as ShiftCapsPayload);
      } catch (validateErr) {
        if (isValidationError(validateErr)) {
          if (hasOnlyMissingAgentErrors(validateErr.errors)) {
            const agentIds = parseMissingAgentIds(validateErr.errors);
            if (agentIds) {
              await refetchWorkers();
              setModalState({
                type: "missingAgents",
                agentIds,
                retry: { type: "upload", file: modalState.file },
              });
              return;
            }
          }
          setModalState({
            type: "uploadResult",
            result: {
              success: false,
              message: "CSV validation failed.",
              errors: formatNonAgentValidationErrors(validateErr.errors, workers),
            },
          });
        } else {
          setModalState({
            type: "uploadResult",
            result: {
              success: false,
              message: "Failed to upload spreadsheet",
              errors: ["An unexpected error occurred."],
            },
          });
        }
        return;
      }

      try {
        if (validatedData.length > 0) {
          await allocationsService.submitShiftCaps(validatedData, shiftDate);
        }
      } catch (submitErr) {
        if (isValidationError(submitErr)) {
          if (hasOnlyMissingAgentErrors(submitErr.errors)) {
            const agentIds = parseMissingAgentIds(submitErr.errors);
            if (agentIds) {
              await refetchWorkers();
              setModalState({
                type: "missingAgents",
                agentIds,
                retry: {
                  type: "upload",
                  file: modalState.file,
                  validatedData,
                },
              });
              return;
            }
          }
          setModalState({
            type: "uploadResult",
            result: {
              success: false,
              message: "CSV validation failed.",
              errors: formatNonAgentValidationErrors(submitErr.errors, workers),
            },
          });
        } else {
          setModalState({
            type: "uploadResult",
            result: {
              success: false,
              message: "Failed to upload spreadsheet",
              errors: ["An unexpected error occurred."],
            },
          });
        }
        return;
      }

      setModalState({
        type: "uploadResult",
        result: {
          success: true,
          message: "Successfully processed CSV file.",
          processedCount: validatedData.length,
          failedCount: 0,
        },
      });
      await fetchData();
    } finally {
      setIsSaving(false);
    }
  }, [modalState, shiftDate, workers, fetchData, refetchWorkers]);

  const missingAgentsForModal = useMemo((): MissingAgent[] => {
    if (modalState.type !== "missingAgents") return [];
    return modalState.agentIds.map((id) => ({
      userId: id,
      name: workers.find((w) => w.userId === id)?.name ?? String(id),
    }));
  }, [modalState, workers]);

  const handleAddMissingAgents = useCallback(
    async (agentCaps: ShiftCapsPayload[]) => {
      if (modalState.type !== "missingAgents") return;
      const { retry } = modalState;

      setIsSaving(true);
      try {
        if (retry.type === "edit") {
          const shiftCapsData: ShiftCapsPayload[] = [retry.capsPayload, ...agentCaps];
          await allocationsService.submitShiftCaps(shiftCapsData, shiftDate);

          const { userId, payload } = retry;
          const worker = getWorkerData(userId);
          const updates: Partial<WorkerAllocation> = {};
          if (payload.allocationSkills) {
            updates.allocationSkills = payload.allocationSkills;
          }
          if (retry.capsPayload.shiftStart !== null) {
            updates.shiftStart = retry.capsPayload.shiftStart;
          }
          if (retry.capsPayload.shiftEnd !== null) {
            updates.shiftEnd = retry.capsPayload.shiftEnd;
          }
          setOriginalData((prev) => prev.map((w) => (w.userId === userId ? { ...w, ...updates } : w)));

          if (payload.skillIds) {
            await allocationsService.updateUserSkills({
              userId,
              primarySkillIds: payload.skillIds,
              secondarySkillIds: [],
              originalTarget: 0,
              newTarget: 0,
            });
            setUserSkillAssignments((prev) => {
              const filtered = prev.filter((a) => a.intranetUserId !== userId);
              const newAssignments = payload.skillIds!.map((skillId) => {
                const skill = availableSkills.find((s) => s.allocationSkillId === skillId);
                return {
                  intranetUserId: userId,
                  allocationSkillId: skillId,
                  isPrimary: true,
                  allocationSkillTypeId: skill?.allocationSkillTypeId ?? 0,
                  numberOfAllocations: 0,
                  cap: null,
                };
              });
              return [...filtered, ...newAssignments];
            });
          }

          closeModal();
          toast.success(`Changes saved${worker ? ` for ${worker.name}` : ""}`);
        } else if (retry.type === "upload") {
          const csvData = retry.validatedData ?? (await parseCsvToShiftCaps(retry.file));

          const agentCapsMap = new Map(agentCaps.map((a) => [a.userId, a]));
          const shiftCapsData: ShiftCapsPayload[] = csvData.map((data) => {
            const override = agentCapsMap.get(data.userId);
            if (override) {
              agentCapsMap.delete(data.userId);
              return override;
            }
            return data;
          });
          for (const agent of agentCapsMap.values()) {
            shiftCapsData.push(agent);
          }
          if (shiftCapsData.length > 0) {
            await allocationsService.submitShiftCaps(shiftCapsData, shiftDate);
          }
          setModalState({
            type: "uploadResult",
            result: {
              success: true,
              message: "Successfully processed CSV file.",
              processedCount: csvData.length,
              failedCount: 0,
            },
          });
          await fetchData();
        }
      } catch (err) {
        if (isValidationError(err)) {
          if (hasOnlyMissingAgentErrors(err.errors)) {
            const agentIds = parseMissingAgentIds(err.errors);
            if (agentIds) {
              setModalState({
                type: "missingAgents",
                agentIds,
                retry,
              });
              return;
            }
          }

          if (retry.type === "edit") {
            toast.error(formatNonAgentValidationErrors(err.errors, workers).join(". "));
            closeModal();
            return;
          }

          setModalState({
            type: "uploadResult",
            result: {
              success: false,
              message: "CSV validation failed.",
              errors: formatNonAgentValidationErrors(err.errors, workers),
            },
          });
          return;
        }
        console.error("Failed to add missing agents:", err);
        toast.error("Failed to add missing agents");
        closeModal();
      } finally {
        setIsSaving(false);
      }
    },
    [modalState, shiftDate, workers, availableSkills, closeModal, fetchData, getWorkerData]
  );

  const hasCapsData = useMemo(() => {
    return sortedData.some((w) => Object.keys(w.allocationSkills).length > 0);
  }, [sortedData]);

  if (!canView) return <Navigate to="/pipeline" replace />;

  return (
    <div className="flex h-full flex-col">
      <AllocationsHeader />

      <AllocationsFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedDepartments={selectedDepartments}
        onDepartmentsChange={setSelectedDepartments}
        departments={allowedDepartments}
        selectedSkills={selectedSkills}
        onSkillsChange={setSelectedSkills}
        skillOptions={skillOptions}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        shiftDate={shiftDate}
        onShiftDateChange={setShiftDate}
        capFilters={capFilters}
        onCapFiltersChange={setCapFilters}
        onUploadClick={handleUploadClick}
        onDownloadTemplateClick={handleDownloadTemplate}
        fileInputRef={fileInputRef}
        onFileChange={handleFileChange}
        hasCapsData={hasCapsData}
      />

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : error ? (
        <div className="mb-12 flex flex-grow flex-col items-center justify-center gap-2">
          <CircleX className="stroke-1 text-rose-700" size={40} />
          <p className="text-sm text-rose-700">{error}</p>
          <Button onClick={fetchData} variant="outline">
            Try again
          </Button>
        </div>
      ) : (
        <div className="flex grow overflow-hidden p-3">
          <div ref={tableRef} className="min-w-full overflow-auto rounded-md border">
            <table className="min-w-full">
              <thead className="sticky top-0 z-10">
                <tr className="relative">
                  <Th />
                  <Th sortKey="name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort}>
                    Agent
                  </Th>
                  <Th className="text-center">Shift</Th>
                  <Th>Caps</Th>
                  <Th>Skills Profile</Th>
                  <Th />
                  <td className="absolute inset-x-0 bottom-0 h-px bg-slate-200 p-0" />
                </tr>
              </thead>
              <tbody>
                {sortedData.map((worker) => (
                  <WorkerRow
                    key={worker.userId}
                    worker={worker}
                    skillsByType={getUserSkillsByType(worker.userId)}
                    onReceivingLeadsChange={(receivingLeads) =>
                      handleReceivingLeadsChange(worker.userId, receivingLeads)
                    }
                    onEdit={handleEditAgent}
                  />
                ))}
              </tbody>
            </table>
            {sortedData.length === 0 && (
              <div className="text-muted-foreground flex items-center justify-center py-12 text-sm">
                No caps data found for the selected date.
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmUploadModal
        open={modalState.type === "confirmUpload"}
        onClose={closeModal}
        fileName={modalState.type === "confirmUpload" ? modalState.file.name : ""}
        isSaving={isSaving}
        onConfirm={handleConfirmUpload}
      />

      <UploadResultModal
        open={modalState.type === "uploadResult"}
        onClose={closeModal}
        result={modalState.type === "uploadResult" ? modalState.result : undefined}
      />

      <MissingAgentsModal
        open={modalState.type === "missingAgents"}
        onClose={closeModal}
        isSaving={isSaving}
        onConfirm={handleAddMissingAgents}
        agents={missingAgentsForModal}
      />

      <EditAgentModal
        open={modalState.type === "editAgent"}
        onClose={closeModal}
        worker={editAgentWorker}
        availableSkills={availableSkills}
        userSkillIds={editAgentUserSkillIds}
        isSaving={isSavingAgent}
        onSave={handleSaveAgent}
      />
    </div>
  );
};
